'use client';

import React, { useState } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';

const TESTIMONIALS = [
{
  id: 'test-001',
  name: 'Priya Sharma',
  role: 'Homeowner, Tooting',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_120520c96-1763298634313.png",
  avatarAlt: 'Priya Sharma, Indian woman smiling in casual clothing',
  rating: 5,
  text: 'Found a Gas Safe engineer in under 10 minutes. He came the same afternoon, sorted our boiler, and left the place spotless. The review system is genuinely trustworthy — not like other sites.',
  trade: 'Gas Engineer',
  date: '2 weeks ago'
},
{
  id: 'test-002',
  name: 'David Okonkwo',
  role: 'Homeowner, Brixton',
  avatar: "https://images.unsplash.com/photo-1555311541-539c2b1b1ee2",
  avatarAlt: 'David Okonkwo, Black man in a light blue shirt',
  rating: 5,
  text: 'I just typed "need someone to rewire my kitchen extension in SW2" and within seconds I had a list of NICEIC-certified electricians nearby. Hired Sarah Chen — brilliant job, great price.',
  trade: 'Electrician',
  date: '1 month ago'
},
{
  id: 'test-003',
  name: 'James Hartley',
  role: 'Plumber (Provider), Clapham',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_11c8c04d2-1772209925689.png",
  avatarAlt: 'James Hartley, male plumber in blue workwear',
  rating: 5,
  text: 'The overflow exchange is a game changer. I passed on 3 jobs last month when I was fully booked — earned my credits back plus got 2 new customers through the platform. Worth every penny of Pro.',
  trade: 'Provider — Plumber',
  date: '3 weeks ago'
},
{
  id: 'test-004',
  name: 'Fatima Al-Hassan',
  role: 'Homeowner, Didsbury',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1532e4bc9-1772092408306.png",
  avatarAlt: 'Fatima Al-Hassan, woman smiling warmly',
  rating: 5,
  text: 'After a nightmare experience on another platform, LoveLocals restored my faith. Every provider I contacted had real, detailed reviews — not vague stars. Booked a builder for our extension, no regrets.',
  trade: 'Builder',
  date: '5 days ago'
}];


export default function TestimonialsSection() {
  const [activeIdx, setActiveIdx] = useState(0);

  const prev = () => setActiveIdx((i) => i === 0 ? TESTIMONIALS?.length - 1 : i - 1);
  const next = () => setActiveIdx((i) => i === TESTIMONIALS?.length - 1 ? 0 : i + 1);

  const active = TESTIMONIALS?.[activeIdx];

  return (
    <section
      className="py-20 relative overflow-hidden"
      style={{ backgroundColor: '#250C31' }}>

      {/* Subtle background orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-0 w-64 h-64 rounded-full blur-3xl"
          style={{ backgroundColor: 'rgba(58,32,71,0.5)' }} />

        <div
          className="absolute bottom-0 right-0 w-64 h-64 rounded-full blur-3xl"
          style={{ backgroundColor: 'rgba(58,32,71,0.4)' }} />

      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-3">
            Trusted by thousands
          </h2>
          <p className="text-lg" style={{ color: 'rgba(153,230,200,0.70)' }}>
            Real reviews from real homeowners and providers.
          </p>
        </div>

        {/* Featured testimonial */}
        <div className="max-w-2xl mx-auto mb-8">
          <div
            className="rounded-2xl p-8 relative"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.10)'
            }}>

            <Quote size={32} className="mb-4 opacity-60" style={{ color: 'rgba(153,230,200,0.60)' }} />
            <p className="text-white/90 text-lg leading-relaxed mb-6 italic">
              &ldquo;{active?.text}&rdquo;
            </p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AppImage
                  src={active?.avatar}
                  alt={active?.avatarAlt}
                  width={44}
                  height={44}
                  className="w-11 h-11 rounded-full object-cover" />

                <div>
                  <p className="text-white font-semibold text-sm">{active?.name}</p>
                  <p className="text-xs" style={{ color: 'rgba(153,230,200,0.60)' }}>{active?.role}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-0.5 justify-end mb-0.5">
                  {Array.from({ length: active?.rating })?.map((_, si) =>
                  <Star key={`star-${active?.id}-${si}`} size={13} className="text-amber-400 fill-amber-400" />
                  )}
                </div>
                <p className="text-xs" style={{ color: 'rgba(153,230,200,0.50)' }}>
                  {active?.trade} · {active?.date}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={prev}
              className="w-9 h-9 rounded-full flex items-center justify-center transition-all"
              style={{ border: '1px solid rgba(255,255,255,0.20)', color: 'rgba(255,255,255,0.60)' }}
              aria-label="Previous testimonial">

              <ChevronLeft size={16} />
            </button>
            <div className="flex items-center gap-2">
              {TESTIMONIALS?.map((_, i) =>
              <button
                key={`dot-${i}`}
                onClick={() => setActiveIdx(i)}
                className="rounded-full transition-all duration-200"
                style={{
                  width: i === activeIdx ? '24px' : '8px',
                  height: '8px',
                  backgroundColor: i === activeIdx ? '#99E6C8' : 'rgba(255,255,255,0.20)'
                }}
                aria-label={`Go to testimonial ${i + 1}`} />

              )}
            </div>
            <button
              onClick={next}
              className="w-9 h-9 rounded-full flex items-center justify-center transition-all"
              style={{ border: '1px solid rgba(255,255,255,0.20)', color: 'rgba(255,255,255,0.60)' }}
              aria-label="Next testimonial">

              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Mini cards row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-3xl mx-auto">
          {TESTIMONIALS?.map((t, i) =>
          <button
            key={t?.id}
            onClick={() => setActiveIdx(i)}
            className="flex items-center gap-2 p-3 rounded-xl border transition-all duration-150 text-left"
            style={{
              background: i === activeIdx ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.03)',
              borderColor: i === activeIdx ? 'rgba(153,230,200,0.30)' : 'rgba(255,255,255,0.10)'
            }}>

              <AppImage
              src={t?.avatar}
              alt={t?.avatarAlt}
              width={32}
              height={32}
              className="w-8 h-8 rounded-full object-cover flex-shrink-0" />

              <div className="min-w-0">
                <p className="text-white/80 text-xs font-medium truncate">{t?.name}</p>
                <p className="text-xs truncate" style={{ color: 'rgba(153,230,200,0.50)' }}>{t?.trade}</p>
              </div>
            </button>
          )}
        </div>
      </div>
    </section>);

}